import { useCallback, useEffect } from 'react'

import { Timeout } from '../../../types/timeout'

const AUTOFOCUS_TIMEOUT = 250

// `setSelectionRange` throws an InvalidStateError on any other input type (date, number, checkbox…).
const SELECTABLE_INPUT_TYPES = ['text', 'search', 'url', 'tel', 'password', 'textarea']

export const useFocusOnFirstInput = (container: HTMLElement | null, shouldFocus?: boolean) => {
  const focusAndMoveCursorToTheEnd = useCallback((element: HTMLInputElement | HTMLTextAreaElement) => {
    const end = element.value?.length

    if (SELECTABLE_INPUT_TYPES.includes(element.type)) {
      element.setSelectionRange?.(end, end)
    }

    element.focus()
  }, [])

  useEffect(() => {
    let autofocusTimeout: Timeout

    if (container && shouldFocus) {
      autofocusTimeout = setTimeout(() => {
        // The user may already be in a field by the time the delay runs out; moving focus would redirect their typing.
        if (container.contains(document.activeElement)) {
          return
        }

        const focusableElement = container.querySelectorAll(
          'input:not(:disabled):not([readonly]):not([type=hidden]),textarea:not(:disabled):not([readonly])',
        )?.[0] as undefined | HTMLInputElement | HTMLTextAreaElement

        if (focusableElement) {
          focusAndMoveCursorToTheEnd(focusableElement)
        }
      }, AUTOFOCUS_TIMEOUT)

      return () => {
        if (autofocusTimeout) {
          clearTimeout(autofocusTimeout)
        }
      }
    }
  }, [container, focusAndMoveCursorToTheEnd, shouldFocus])
}
